import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  BarChart3,
  User,
  LogOut,
  Settings,
  TrendingUp,
  Home,
  ChevronDown,
  Calendar,
  Target,
  Workflow,
  Link,
  Users,
  Clock,
  Shield
} from "lucide-react";
import { getUser, signOut, isAdmin, canAccessStrategicMap, canAccessScorecard, canAccessIndicatorValues } from "../services/auth";
import ChangePasswordModal from "./ChangePasswordModal";
import marplyLogo from "../assets/marply-logo.png";

const Header = ({
  indicators,
  kpiSummary,
  dataSource,
  openDashboard,
  openStrategicMap,
  onOpenBalancedScorecard,
  openPerspectiveManagement,
  openObjectiveManagement,
  openIndicatorManagement,
  openPeriodManagement,
  openIndicatorValueManagement,
  openObjectiveLinkManagement,
  openUserManagement,
  openStrategicFlow,
  openAccessLogs,
  onLogoutStart
}) => {
  const [user, setUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showConfigMenu, setShowConfigMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const userMenuRef = useRef(null);
  const configMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Carregar dados do usuário
  useEffect(() => {
    const loadUser = () => {
      try {
        const currentUser = getUser();
        if (currentUser) {
          setUser(currentUser);
          console.log('👤 Usuário carregado:', currentUser.username);
        } else {
          console.warn('⚠️ Nenhum usuário encontrado');
          setUser({ username: 'Usuário', role: 'user', is_admin: false });
        }
      } catch (error) {
        console.error('❌ Erro ao carregar usuário:', error);
        setUser({ username: 'Usuário', role: 'user', is_admin: false });
      }
    };

    loadUser();
  }, []);

  // Fechar menus ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (configMenuRef.current && !configMenuRef.current.contains(event.target)) {
        setShowConfigMenu(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setShowMobileMenu(false);
      }
    };

    if (showUserMenu || showConfigMenu || showMobileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserMenu, showConfigMenu, showMobileMenu]);

  // Fechar menus com ESC
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowUserMenu(false);
        setShowConfigMenu(false);
        setShowMobileMenu(false);
      }
    };

    if (showUserMenu || showConfigMenu || showMobileMenu) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [showUserMenu, showConfigMenu, showMobileMenu]);

  // Função para alteração de senha
  const onPasswordChangeClick = useCallback(() => {
    console.log('🔑 Tentando abrir modal de alteração de senha');

    // Fechar todos os menus
    setShowUserMenu(false);
    setShowConfigMenu(false);
    setShowMobileMenu(false);

    // Abrir modal com pequeno delay
    setTimeout(() => {
      console.log('🔑 Abrindo modal de alteração de senha');
      setShowChangePassword(true);
    }, 100);
  }, []);

  // Função para logout
  const onLogoutClick = useCallback(async () => {
    if (isLoggingOut) {
      console.log('🚪 Logout já em progresso, ignorando');
      return;
    }

    console.log('🚪 Iniciando processo de logout');
    setIsLoggingOut(true);

    // Fechar todos os menus
    setShowUserMenu(false);
    setShowConfigMenu(false);
    setShowMobileMenu(false);

    try {
      if (onLogoutStart) {
        onLogoutStart(true);
      }

      // Executar logout
      console.log('🚪 Chamando signOut');
      signOut();

    } catch (error) {
      console.error('❌ Erro durante logout:', error);
      setIsLoggingOut(false);

      if (onLogoutStart) {
        onLogoutStart(false);
      }

      // Fallback: limpar tudo e recarregar
      try {
        sessionStorage.clear();
        localStorage.clear();
        window.location.reload();
      } catch (fallbackError) {
        console.error('❌ Erro no fallback:', fallbackError);
        window.location.href = '/';
      }
    }
  }, [isLoggingOut, onLogoutStart]);

  const closeAllMenus = () => {
    setShowUserMenu(false);
    setShowConfigMenu(false);
    setShowMobileMenu(false);
  };

  const hasAdminAccess = () => {
    return user && (user.is_admin === true || user.role === 'admin' || isAdmin());
  };

  const canUserAccessStrategicMap = () => {
    return hasAdminAccess() || canAccessStrategicMap();
  };

  const canUserAccessScorecard = () => {
    return hasAdminAccess() || canAccessScorecard();
  };

  const canUserAccessIndicatorValues = () => {
    return hasAdminAccess() || canAccessIndicatorValues();
  };

  const getUserDisplayName = () => {
    return user?.username || user?.name || 'Usuário';
  };

  const getUserRole = () => {
    if (user?.is_admin || user?.role === 'admin') return 'Administrador';
    return user?.role || 'Usuário';
  };

  return (
    <>
      <header className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white shadow-lg sticky top-0 z-50 border-b border-slate-700/50">
        <div className="px-2 sm:px-4 lg:px-6 py-2 lg:py-3">
          <div className="flex justify-between items-center">
            {/* Logo e Título */}
            <div className="flex items-center gap-3 lg:gap-4">
              <div className="rounded-xl bg-white px-3 py-2 shadow-md">
                <img src={marplyLogo} alt="Marply" className="h-6 lg:h-7 w-auto" />
              </div>
              <div className="hidden md:flex flex-col leading-tight">
                <h1 className="text-base lg:text-xl font-semibold text-white tracking-tight">
                  Balanced Scorecard
                </h1>
                <p className="text-slate-400 text-xs">
                  Gestão estratégica · Marply
                </p>
              </div>
            </div>

            {/* Menu Desktop */}
            <div className="hidden lg:flex items-center gap-2 xl:gap-4">
              <div className="flex items-center gap-2 xl:gap-4">
                {/* Navegação Principal Desktop */}
                <div className="flex gap-1.5 xl:gap-2">
                  <button
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-sm text-slate-100 bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition-colors disabled:opacity-50"
                    onClick={() => window.location.reload()}
                    disabled={isLoggingOut}
                  >
                    <Home className="w-3.5 h-3.5" />
                    Inicial
                  </button>

                  {(
                    <button
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-sm text-slate-100 bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition-colors disabled:opacity-50"
                      onClick={openStrategicMap}
                      disabled={isLoggingOut}
                    >
                      <Target className="w-3.5 h-3.5" />
                      <span className="hidden xl:inline">Mapa Estratégico</span>
                      <span className="xl:hidden">Mapa</span>
                    </button>
                  )}

                  {(
                    <button
                      onClick={openStrategicFlow}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-sm text-slate-100 bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition-colors disabled:opacity-50"
                      disabled={isLoggingOut}
                    >
                      <Workflow className="w-3.5 h-3.5" />
                      <span className="hidden xl:inline">Fluxo Estratégico</span>
                      <span className="xl:hidden">Fluxo</span>
                    </button>
                  )}

                  {(
                    <button
                      onClick={onOpenBalancedScorecard}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-sm text-slate-100 bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition-colors disabled:opacity-50"
                      disabled={isLoggingOut}
                    >
                      <BarChart3 className="w-3.5 h-3.5" />
                      Scorecard
                    </button>
                  )}

                  {/* Menu Configurações - Apenas para Admin */}
                  {hasAdminAccess() && (
                    <div className="relative" ref={configMenuRef}>
                      <button
                        onClick={() => setShowConfigMenu(!showConfigMenu)}
                        disabled={isLoggingOut}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-sm text-slate-100 bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition-colors disabled:opacity-50"
                      >
                        <Settings className="w-3.5 h-3.5" />
                        <span className="hidden xl:inline">Configurações</span>
                        <span className="xl:hidden">Config</span>
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${showConfigMenu ? 'rotate-180' : ''}`} />
                      </button>

                      {showConfigMenu && (
                        <div className="absolute right-0 mt-2 w-56 bg-white/95 backdrop-blur-md rounded-xl shadow-2xl border border-slate-200/50 py-2 z-50 animate-in slide-in-from-top-2 duration-300">
                          <div className="px-3 py-2 border-b border-slate-100">
                            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wide">Gestão do Sistema</h3>
                          </div>

                          <button
                            onClick={() => {
                              setShowConfigMenu(false);
                              if (openPerspectiveManagement) openPerspectiveManagement();
                            }}
                            className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 flex items-center transition-all duration-200 font-medium text-sm"
                          >
                            <Target className="w-3.5 h-3.5 mr-2 text-blue-500" />
                            Perspectivas
                          </button>

                          <button
                            onClick={() => {
                              setShowConfigMenu(false);
                              if (openObjectiveManagement) openObjectiveManagement();
                            }}
                            className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 flex items-center transition-all duration-200 font-medium text-sm"
                          >
                            <Target className="w-3.5 h-3.5 mr-2 text-emerald-500" />
                            Objetivos
                          </button>

                          <button
                            onClick={() => {
                              setShowConfigMenu(false);
                              if (openObjectiveLinkManagement) openObjectiveLinkManagement();
                            }}
                            className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 flex items-center transition-all duration-200 font-medium text-sm"
                          >
                            <Link className="w-3.5 h-3.5 mr-2 text-cyan-500" />
                            Vínculos
                          </button>

                          <button
                            onClick={() => {
                              setShowConfigMenu(false);
                              if (openIndicatorManagement) openIndicatorManagement();
                            }}
                            className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 flex items-center transition-all duration-200 font-medium text-sm"
                          >
                            <BarChart3 className="w-3.5 h-3.5 mr-2 text-purple-500" />
                            Indicadores
                          </button>

                          <button
                            onClick={() => {
                              setShowConfigMenu(false);
                              if (openPeriodManagement) openPeriodManagement();
                            }}
                            className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 flex items-center transition-all duration-200 font-medium text-sm"
                          >
                            <Calendar className="w-3.5 h-3.5 mr-2 text-orange-500" />
                            Períodos
                          </button>

                          {hasAdminAccess() && (
                            <>
                              <button
                                onClick={() => {
                                  setShowConfigMenu(false);
                                  if (openUserManagement) openUserManagement();
                                }}
                                className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 flex items-center transition-all duration-200 font-medium text-sm"
                              >
                                <Users className="w-3.5 h-3.5 mr-2 text-purple-500" />
                                Usuários
                              </button>

                              <button
                                onClick={() => {
                                  setShowConfigMenu(false);
                                  if (openAccessLogs) openAccessLogs();
                                }}
                                className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 flex items-center transition-all duration-200 font-medium text-sm"
                              >
                                <Clock className="w-3.5 h-3.5 mr-2 text-orange-500" />
                                Logs
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Lançamento de Valores */}
                  {(
                    <button
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-sm text-slate-100 bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition-colors disabled:opacity-50"
                      onClick={openIndicatorValueManagement}
                      disabled={isLoggingOut}
                    >
                      <TrendingUp className="w-3.5 h-3.5" />
                      <span className="hidden xl:inline">Lançamento</span>
                      <span className="xl:hidden">Valores</span>
                    </button>
                  )}
                </div>

                {/* Menu do Usuário Desktop */}
                <div className="relative ml-2 pl-2 border-l border-slate-600" ref={userMenuRef}>
                  <button
                    onClick={() => !isLoggingOut && setShowUserMenu(!showUserMenu)}
                    disabled={isLoggingOut}
                    className="flex items-center gap-2 text-white hover:bg-white/10 px-3 py-2 rounded-lg transition-all duration-300 group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center shadow-md">
                      {hasAdminAccess() ? (
                        <Shield className="w-4 h-4 text-white" />
                      ) : (
                        <User className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div className="flex flex-col items-start">
                      <span className="font-medium text-xs">{getUserDisplayName()}</span>
                      <span className="text-xs text-slate-300 bg-slate-700/50 px-1.5 py-0.5 rounded uppercase tracking-wide">
                        {getUserRole()}
                      </span>
                    </div>
                    {!isLoggingOut && (
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${showUserMenu ? 'rotate-180' : ''}`} />
                    )}
                  </button>

                  {showUserMenu && !isLoggingOut && (
                    <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-2xl border border-slate-200 py-2 z-[9999] animate-in slide-in-from-top-2 duration-300">
                      <div className="px-3 py-2 border-b border-slate-100">
                        <p className="font-medium text-slate-800 text-sm">{getUserDisplayName()}</p>
                        <p className="text-xs text-slate-500 uppercase tracking-wide flex items-center gap-1">
                          {hasAdminAccess() && <Shield className="w-3 h-3" />}
                          {getUserRole()}
                        </p>
                      </div>

                      <button
                        onClick={onPasswordChangeClick}
                        disabled={isLoggingOut}
                        className="flex items-center w-full px-3 py-2 text-sm text-slate-700 hover:bg-blue-100 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      >
                        <Settings className="w-3.5 h-3.5 mr-2 text-blue-500" />
                        Alterar Senha
                      </button>

                      <hr className="my-1 border-slate-200" />

                      <button
                        onClick={onLogoutClick}
                        disabled={isLoggingOut}
                        className="flex items-center w-full px-3 py-2 text-sm text-red-600 hover:bg-red-100 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      >
                        <LogOut className="w-3.5 h-3.5 mr-2" />
                        {isLoggingOut ? 'Saindo...' : 'Sair'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Botões Mobile */}
            <div className="lg:hidden flex items-center gap-3">
              {/* Info do usuário mobile (apenas visual) */}
              <div className="flex items-center text-white p-2.5 rounded-xl">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center mr-2">
                  {hasAdminAccess() ? (
                    <Shield className="w-4 h-4 text-white" />
                  ) : (
                    <User className="w-4 h-4 text-white" />
                  )}
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-semibold">{getUserDisplayName()}</span>
                  <span className="text-xs text-slate-300 uppercase tracking-wide">
                    {getUserRole()}
                  </span>
                </div>
              </div>

              {/* Botão Hambúrguer */}
              <button
                onClick={() => !isLoggingOut && setShowMobileMenu(!showMobileMenu)}
                disabled={isLoggingOut}
                className="text-white hover:bg-white/10 p-2.5 rounded-xl transition-all duration-300 disabled:opacity-50"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Menu Mobile */}
        {showMobileMenu && !isLoggingOut && (
          <div className="lg:hidden mt-4 pb-4 border-t border-slate-600/50" ref={mobileMenuRef}>
            <div className="flex flex-col space-y-3 pt-4 px-2">
              <button
                className="w-full flex items-center gap-3 px-5 py-3.5 rounded-xl font-medium text-slate-100 bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition-colors"
                onClick={() => {
                  window.location.reload();
                  setShowMobileMenu(false);
                }}
              >
                <Home className="w-5 h-5" />
                Inicial
              </button>

              {(
                <button
                  className="w-full flex items-center gap-3 px-5 py-3.5 rounded-xl font-medium text-slate-100 bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition-colors"
                  onClick={() => {
                    openStrategicMap();
                    setShowMobileMenu(false);
                  }}
                >
                  <Target className="w-5 h-5" />
                  Mapa Estratégico
                </button>
              )}

              {(
                <button
                  className="w-full flex items-center gap-3 px-5 py-3.5 rounded-xl font-medium text-slate-100 bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition-colors"
                  onClick={() => {
                    openStrategicFlow();
                    setShowMobileMenu(false);
                  }}
                >
                  <Workflow className="w-5 h-5" />
                  Fluxo Estratégico
                </button>
              )}

              {(
                <button
                  className="w-full flex items-center gap-3 px-5 py-3.5 rounded-xl font-medium text-slate-100 bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition-colors"
                  onClick={() => {
                    openIndicatorValueManagement();
                    setShowMobileMenu(false);
                  }}
                >
                  <TrendingUp className="w-5 h-5" />
                  Lançamento de Valores
                </button>
              )}

              {(
                <button
                  onClick={() => {
                    onOpenBalancedScorecard();
                    setShowMobileMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-5 py-3.5 rounded-xl font-medium text-slate-100 bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition-colors"
                >
                  <BarChart3 className="w-5 h-5" />
                  Scorecard
                </button>
              )}

              {/* Seção Configurações Mobile - Apenas para Admin */}
              {hasAdminAccess() && (
                <div className="pt-4 mt-4 border-t border-slate-600/50">
                  <div className="mb-3 px-3">
                    <h3 className="text-slate-300 text-sm font-bold uppercase tracking-wide flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      Configurações
                    </h3>
                  </div>

                  <button
                    className="w-full text-left text-white hover:bg-white/10 px-5 py-3 rounded-xl flex items-center gap-3 transition-all duration-300 font-medium"
                    onClick={() => {
                      if (openPerspectiveManagement) openPerspectiveManagement();
                      setShowMobileMenu(false);
                    }}
                  >
                    <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                      <Target className="w-4 h-4 text-blue-400" />
                    </div>
                    Gestão de Perspectivas
                  </button>

                  <button
                    className="w-full text-left text-white hover:bg-white/10 px-5 py-3 rounded-xl flex items-center gap-3 transition-all duration-300 font-medium"
                    onClick={() => {
                      if (openObjectiveManagement) openObjectiveManagement();
                      setShowMobileMenu(false);
                    }}
                  >
                    <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                      <Target className="w-4 h-4 text-emerald-400" />
                    </div>
                    Gestão de Objetivos
                  </button>

                  <button
                    className="w-full text-left text-white hover:bg-white/10 px-5 py-3 rounded-xl flex items-center gap-3 transition-all duration-300 font-medium"
                    onClick={() => {
                      if (openObjectiveLinkManagement) openObjectiveLinkManagement();
                      setShowMobileMenu(false);
                    }}
                  >
                    <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                      <Link className="w-4 h-4 text-cyan-400" />
                    </div>
                    Vínculos de Objetivos
                  </button>

                  <button
                    className="w-full text-left text-white hover:bg-white/10 px-5 py-3 rounded-xl flex items-center gap-3 transition-all duration-300 font-medium"
                    onClick={() => {
                      if (openIndicatorManagement) openIndicatorManagement();
                      setShowMobileMenu(false);
                    }}
                  >
                    <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                      <BarChart3 className="w-4 h-4 text-purple-400" />
                    </div>
                    Gestão de Indicadores
                  </button>

                  <button
                    className="w-full text-left text-white hover:bg-white/10 px-5 py-3 rounded-xl flex items-center gap-3 transition-all duration-300 font-medium"
                    onClick={() => {
                      if (openPeriodManagement) openPeriodManagement();
                      setShowMobileMenu(false);
                    }}
                  >
                    <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center">
                      <Calendar className="w-4 h-4 text-orange-400" />
                    </div>
                    Gestão de Períodos
                  </button>

                  {hasAdminAccess() && (
                    <>
                      <button
                        className="w-full text-left text-white hover:bg-white/10 px-5 py-3 rounded-xl flex items-center gap-3 transition-all duration-300 font-medium"
                        onClick={() => {
                          if (openUserManagement) openUserManagement();
                          setShowMobileMenu(false);
                        }}
                      >
                        <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                          <Users className="w-4 h-4 text-purple-400" />
                        </div>
                        Gestão de Usuários
                      </button>

                      <button
                        className="w-full text-left text-white hover:bg-white/10 px-5 py-3 rounded-xl flex items-center gap-3 transition-all duration-300 font-medium"
                        onClick={() => {
                          if (openAccessLogs) openAccessLogs();
                          setShowMobileMenu(false);
                        }}
                      >
                        <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center">
                          <Clock className="w-4 h-4 text-orange-400" />
                        </div>
                        Logs de Acesso
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Modal de Alteração de Senha */}
      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />
    </>
  );
};

export default Header;